import { NetworkStatus, useLazyQuery, useMutation } from '@apollo/client';
import { Box, ToastAnimateFrom, ToastContent, ToastPosition, ToastType } from '@bechtel/gds';
import {
  DataType,
  FeedbackJumperInput,
  FeedbackJumperResult,
  MetadataColumn,
  UiMessageKey,
  UiTextEnum,
} from '@bechtel/sr-types';
import { Formik } from 'formik';
import { get, omit } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { number, object, string } from 'yup';
import { FormSkeleton, useLanguage, useTableMetadataContext } from '~/components';
import MessageModal from '~/components/organisms/message-modal/message';
import { FEEDBACK_JUMPER, SEARCH_JUMPERS } from '~/gql';
import { usePermissions, useToast } from '~/hooks';
import { getDescription } from '~/utilities';
import { RightPanelStyled } from '~/views/commodities/styled';
import { AdditionalInfo } from './additional-info';
import JumperFeedbackForm from './form';
import { JumperFeedbackFormikState } from './form/types';
import { getFormikData, jumperFeedbackInitialValues } from './utils';

const getYupSchema = (
  isAlternateLanguageUsed,
  getMultiLingualTextByKey,
  data: MetadataColumn[],
  permission: boolean = false,
  entityText: string = '',
  maxConnections: number = 0
) => {
  const constStatusDescription = getDescription(data, isAlternateLanguageUsed, 'constructionStatus');
  const tagDescription = getDescription(data, isAlternateLanguageUsed, 'tag');
  const connMadeDescription = getDescription(data, isAlternateLanguageUsed, 'connectionsMade');

  const constructionStatus = permission
    ? string()
        .required(getMultiLingualTextByKey(UiTextEnum.VALUE_CANNOT_BE_BLANK, [constStatusDescription]))
        .nullable()
    : string();
  const connectionsMade = number()
    .min(
      0,
      getMultiLingualTextByKey(UiTextEnum.VALUE_OUT_OF_RANGE, [connMadeDescription, String(0), String(maxConnections)])
    )
    .max(
      maxConnections,
      getMultiLingualTextByKey(UiTextEnum.VALUE_OUT_OF_RANGE, [connMadeDescription, String(0), String(maxConnections)])
    );

  return {
    tag: string()
      .required(getMultiLingualTextByKey(UiTextEnum.VALUE_CANNOT_BE_BLANK, [tagDescription]))
      .test(
        'tag-check',
        getMultiLingualTextByKey(UiTextEnum.VALUE_IS_NOT_VALID, [getMultiLingualTextByKey(entityText)]),
        function () {
          const { tagInvalid } = this.parent;
          return tagInvalid === false;
        }
      ),
    constructionStatus,
    connectionsMade,
  };
};

const Jumpers = () => {
  const { showToast } = useToast();
  const [initialValues, setInitialValues] = useState<JumperFeedbackFormikState>(jumperFeedbackInitialValues);
  const tableMetadata = useTableMetadataContext();
  const { getMultiLingualTextByKey, isAlternateLanguageUsed } = useLanguage();
  const { canUpdateJumperFeedback } = usePermissions();
  const [searchJumpers, { loading, networkStatus, data: searchJumpersData }] = useLazyQuery(SEARCH_JUMPERS, {
    notifyOnNetworkStatusChange: true,
  });
  const initialValueKeys = useRef(initialValues && Object?.keys(initialValues));
  const jumperData = get(searchJumpersData, 'searchJumpers.items[0]');
  const entityText = getMultiLingualTextByKey('Jumper');
  const maxConnections =
    2 *
    ((jumperData?.cableCode?.numberOfConductors ?? 0) +
      (jumperData?.cableCode?.numberOfShields ?? 0) +
      (jumperData?.cableCode?.numberOfGrounds ?? 0));

  const formikData = useMemo(() => {
    if (!jumperData) return;
    return getFormikData(jumperData, initialValueKeys.current);
  }, [jumperData]);

  useEffect(() => {
    setInitialValues({ ...jumperFeedbackInitialValues, ...formikData });
  }, [formikData]);

  const [feedbackJumper, { data: feedbackJumperData, error: feedbackJumperError }] = useMutation<
    { feedbackJumper: FeedbackJumperResult },
    { feedbackJumperInput: FeedbackJumperInput }
  >(FEEDBACK_JUMPER, {
    onCompleted: (data) => {
      const feedbackData = get(data, 'feedbackJumper');
      if (feedbackJumperError === undefined && data.feedbackJumper.errors == null) {
        showToast(UiTextEnum.SAVED_SUCCESSFULLY_TOAST, [entityText, feedbackData?.jumper?.tag ?? ''], ToastType.INFO);
      }
    },
    onError: () => {
      showToast(UiTextEnum.ERROR_WHILE_SAVING_TOAST, [entityText, ''], ToastType.WARNING);
    },
  });

  const validationSchema = object().shape(
    tableMetadata?.metadataColumns
      ? getYupSchema(
          isAlternateLanguageUsed,
          getMultiLingualTextByKey,
          tableMetadata?.metadataColumns,
          canUpdateJumperFeedback,
          entityText,
          maxConnections
        )
      : {}
  );

  const saveHandler = async (values) => {
    const id = searchJumpersData.searchJumpers.items[0].id;
    await feedbackJumper({
      variables: {
        feedbackJumperInput: { ...values, id },
      },
    });
  };
  const errorKeys: UiMessageKey[] =
    get(feedbackJumperData, 'feedbackJumper.infos') ?? get(feedbackJumperData, 'feedbackJumper.errors') ?? [];

  return (
    <>
      <Formik
        enableReinitialize={true}
        validateOnChange={true}
        validateOnBlur={true}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={async (values, { setSubmitting }) => {
          // Convert empty strings to null
          // @ts-ignore empy objec literal populated with values
          const sanitizedValues = Object.keys(values).reduce((acc, key) => {
            const matchingColumn = tableMetadata?.metadataColumns.find((col) => col.memberName === key);
            const value = matchingColumn?.dataType === DataType.NUMBER ? parseFloat(values[key]) : values[key];
            acc[key] = value === '' ? null : value;
            return acc;
          }, {});
          const updatedValues = omit(sanitizedValues, ['tag', 'tagInvalid']);
          await saveHandler(updatedValues);
          setSubmitting(false);
        }}
      >
        <Box flexDirection={{ sm: 'column' }} flexGrow={{ sm: '1' }}>
          {!tableMetadata ? (
            <FormSkeleton columns={1} rows={8} />
          ) : (
            <JumperFeedbackForm
              searchJumpers={searchJumpers}
              loading={loading || networkStatus === NetworkStatus.refetch}
            />
          )}
        </Box>
      </Formik>
      {errorKeys && errorKeys.length > 0 && <MessageModal messageKeys={errorKeys}></MessageModal>}
      <ToastContent position={ToastPosition.MIDDLE} animateFrom={ToastAnimateFrom.BOTTOM} />
      <RightPanelStyled isCollapsed={true}>
        <AdditionalInfo
          data={jumperData}
          loading={loading || networkStatus === NetworkStatus.refetch}
          error={feedbackJumperError}
        />
      </RightPanelStyled>
    </>
  );
};

export default Jumpers;
